import { useState } from 'react';
import { X, PaperPlaneTilt, Plus, Trash } from '@phosphor-icons/react';
import { sendDocumentByEmail } from '@/lib/documentsApi';
import { isValidEmail, EMAIL_ERROR } from '@/lib/validators';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  documentId: string;
  documentName: string;
  matterName?: string;
  defaultToEmail?: string;
  onClose: () => void;
  onSent?: () => void;
}

export function SendDocumentEmailModal({
  documentId,
  documentName,
  matterName,
  defaultToEmail = '',
  onClose,
  onSent,
}: Props) {
  const defaultSubject = matterName
    ? `${documentName} – ${matterName}`
    : documentName;

  const [toEmail, setToEmail] = useState(defaultToEmail);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function addCc() {
    setCcEmails((prev) => [...prev, '']);
  }

  function updateCc(i: number, val: string) {
    setCcEmails((prev) => prev.map((e, idx) => (idx === i ? val : e)));
  }

  function removeCc(i: number) {
    setCcEmails((prev) => prev.filter((_, idx) => idx !== i));
  }

  // Field-level validation — only flag typed values; empty optional CC rows pass.
  const toEmailError = toEmail.trim().length > 0 && !isValidEmail(toEmail) ? EMAIL_ERROR : null;
  const ccErrors = ccEmails.map((e) =>
    e.trim().length > 0 && !isValidEmail(e) ? EMAIL_ERROR : null,
  );
  const hasEmailErrors = toEmailError !== null || ccErrors.some((e) => e !== null);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!toEmail.trim() || !subject.trim() || hasEmailErrors) return;
    setSending(true);
    setError(null);
    try {
      const cleanCc = ccEmails.map((e) => e.trim()).filter(Boolean);
      await sendDocumentByEmail(documentId, {
        toEmail: toEmail.trim(),
        ccEmails: cleanCc.length > 0 ? cleanCc : undefined,
        subject: subject.trim(),
        message: message.trim() || undefined,
      });
      setSent(true);
      onSent?.();
    } catch {
      setError('Failed to send. Please check the email address and try again.');
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
        <ScrollLock />
        <div className="my-auto w-full max-w-md rounded-lg border border-border bg-background p-8 text-center shadow-xl">
          <PaperPlaneTilt className="mx-auto h-8 w-8 text-primary" weight="fill" />
          <p className="mt-3 text-sm font-semibold text-foreground">Document sent</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {documentName} was sent to {toEmail}.
            The matter timeline has been updated.
          </p>
          <button
            onClick={onClose}
            className="mt-5 rounded-md bg-primary px-4 py-2 text-xs font-medium text-white hover:bg-primary/90"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4">
      <ScrollLock />
      <div className="my-auto w-full max-w-lg rounded-lg border border-border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <p className="text-sm font-semibold text-foreground">Send by email</p>
            <p className="text-xs text-muted-foreground truncate max-w-xs">{documentName}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSend} className="space-y-4 p-6">
          {/* To */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground">To</label>
            <input
              type="email"
              required
              maxLength={255}
              placeholder="john.smith@freshfields.com"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {toEmailError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{toEmailError}</p>}
          </div>

          {/* CC */}
          {ccEmails.length > 0 && (
            <div>
              <label className="mb-1.5 block text-xs font-medium text-foreground">CC</label>
              <div className="space-y-2">
                {ccEmails.map((email, i) => (
                  <div key={i}>
                    <div className="flex items-center gap-2">
                      <input
                        type="email"
                        maxLength={255}
                        placeholder="cc@example.com"
                        value={email}
                        onChange={(e) => updateCc(i, e.target.value)}
                        className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                      <button
                        type="button"
                        onClick={() => removeCc(i)}
                        className="text-muted-foreground hover:text-red-600 dark:hover:text-red-400"
                      >
                        <Trash className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {ccErrors[i] && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{ccErrors[i]}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={addCc}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <Plus className="h-3 w-3" />
            Add CC recipient
          </button>

          {/* Subject */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground">Subject</label>
            <input
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Message */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground">
              Message <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Please find the attached document for your reference…"
              className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          <div className="rounded-md border border-border/50 bg-muted/20 px-3 py-2">
            <p className="text-[10px] text-muted-foreground">
              Sent from <strong>Jurivoxa</strong> on behalf of your chambers.
              The send will be recorded in the matter timeline.
            </p>
          </div>

          {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={sending}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending || !toEmail.trim() || !subject.trim() || hasEmailErrors}
              className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <PaperPlaneTilt className="h-3.5 w-3.5" />
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
